import { makeAutoObservable, runInAction } from "mobx";
import { sendAddToCart, sendRemoveFromCart } from "../../common/tracking";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import type { GAListInformation } from "../../common/types/trackingTypes";

export const CART_QTY_TRACKING_DEBOUNCE_MS = 1000;

type QtyTrackingArgs = {
   sku: string;
   originalQty: number;
   newQty: number;
   position?: number;
   listInformation: GAListInformation;
};

type QtyTrackingSession = {
   sku: string;
   originalQty: number;
   latestQty: number;
   position?: number;
   listInformation: GAListInformation;
   timeoutId: number | null;
};

type CartQtyTrackingStoreType = BaseStoreType & {
   qtyTrackingSessions: Map<string, QtyTrackingSession>;
   startOrUpdateQtyChange(args: QtyTrackingArgs): void;
   flushSession(key: string): void;
   flushAll(): void;
};

const buildSessionKey = (sku: string, listInformation: GAListInformation): string => {
   return `${sku}|${listInformation.type}|${listInformation.name}`;
};

const cartTrackingStore: CartQtyTrackingStoreType = makeAutoObservable({
   qtyTrackingSessions: new Map<string, QtyTrackingSession>(),

   startOrUpdateQtyChange: ({ sku, originalQty, newQty, position, listInformation }: QtyTrackingArgs) => {
      const key = buildSessionKey(sku, listInformation);
      const existing = cartTrackingStore.qtyTrackingSessions.get(key);

      const session: QtyTrackingSession = existing
         ? {
              ...existing,
              latestQty: newQty,
              position,
              listInformation
           }
         : {
              sku,
              originalQty,
              latestQty: newQty,
              position,
              listInformation,
              timeoutId: null
           };

      if (session.timeoutId !== null) {
         clearTimeout(session.timeoutId);
      }

      session.timeoutId = window.setTimeout(() => {
         cartTrackingStore.flushSession(key);
      }, CART_QTY_TRACKING_DEBOUNCE_MS);

      runInAction(() => {
         cartTrackingStore.qtyTrackingSessions.set(key, session);
      });
   },

   flushSession: (key: string) => {
      const session = cartTrackingStore.qtyTrackingSessions.get(key);
      if (!session) {
         return;
      }

      const { sku, originalQty, latestQty, position, listInformation, timeoutId } = session;

      if (timeoutId !== null) {
         clearTimeout(timeoutId);
      }

      const delta = latestQty - originalQty;

      if (delta > 0) {
         sendAddToCart([{ sku, qty: delta }], listInformation);
      } else if (delta < 0) {
         sendRemoveFromCart([{ sku, qty: Math.abs(delta), position }], listInformation);
      }

      runInAction(() => {
         cartTrackingStore.qtyTrackingSessions.delete(key);
      });
   },

   flushAll: () => {
      const keys = Array.from(cartTrackingStore.qtyTrackingSessions.keys());
      for (const key of keys) {
         cartTrackingStore.flushSession(key);
      }
   },

   clearCompanySpecificData: () => {
      cartTrackingStore.flushAll();
   }
});

export default cartTrackingStore;
